import React, { useCallback, useMemo } from 'react';
import { useGameStore } from '../../store/GameStoreContext.ts';
import { useIsPlayerTakingAction } from './useIsPlayerTakingAction.ts';
import { usePlayerVictoryPoints } from './usePlayerVictoryPoints.ts';
// eslint-disable-next-line boundaries/dependencies
import { getLobbyImagePath } from '../../../../../code/client/shared/scripts/ui_utils/Utils.ts';
import { usePlayerStatus } from './usePlayerStatus.ts';
import { useGameTimer } from '../../hooks/useGameTimer.ts';
import { usePlayerInfoSectionContentHandProps } from './sections/usePlayerInfoSectionContentHandProps.tsx';
import { usePlayerInfoSectionContentUsedDevsProps } from './sections/usePlayerInfoSectionContentUsedDevsProps.tsx';
import { usePlayerInfoSectionContentCKProps } from './sections/usePlayerInfoSectionContentCKProps.tsx';
import { usePlayerInfoSectionContentPiecesProps } from './sections/usePlayerInfoSectionContentPiecesProps.tsx';
import { UIGamePlayerInfoSectionContentHand, UIGamePlayerInfoSection, UIGamePlayerInfo, UIGamePlayerInfoSectionContentUsedDevs, UIGamePlayerInfoSectionContentCK, UIGamePlayerInfoSectionContentPieces } from '../UIGamePlayerInfo/index.ts';
import { useCurrentStateValidator } from '../../store/selectors/useCurrentStateValidator.ts';
import { useGameValidator } from '../../store/selectors/useGameValidator.ts';
import { LobbyImage, ModeSettingEnum } from '@colonist/types';
import { TurnState, ActionState } from '@colonist/game-states';
import { usePlayerValidators } from '../../store/selectors/usePlayerValidators.ts';
import { UIGamePlayerOptionsDropdown } from '../../common/UIGamePlayerOptionsDropdown/UIGamePlayerOptionsDropdown.tsx';
import { CurrentStateMessageUtils } from '../UIGameCurrentStatus/CurrentStateMessageUtils.ts';
import { useIsReplay } from '../../store/selectors/useIsReplay.ts';
export const UIGamePlayerInfoContainer = ({ playerColor, visibleSections })=>{
    const modeSetting = useGameStore((state)=>state.gameSettings.modeSetting);
    const myPlayerColor = useGameStore((state)=>state.myColor);
    const isMyPlayer = playerColor === myPlayerColor;
    const userState = useGameStore((state)=>state.gameUserStates.getUserState(playerColor));
    const isConnected = useGameStore((state)=>{
        var _state_gameState_playerStates_playerColor;
        var _state_gameState_playerStates_playerColor_isConnected;
        return (_state_gameState_playerStates_playerColor_isConnected = (_state_gameState_playerStates_playerColor = state.gameState.playerStates[playerColor]) === null || _state_gameState_playerStates_playerColor === void 0 ? void 0 : _state_gameState_playerStates_playerColor.isConnected) !== null && _state_gameState_playerStates_playerColor_isConnected !== void 0 ? _state_gameState_playerStates_playerColor_isConnected : false;
    });
    const isActiveHumanSession = !userState.isBot && isConnected;
    const isReplay = useIsReplay();
    const isPlayerTakingAction = useIsPlayerTakingAction(playerColor);
    const { publicVictoryPoints, privateVictoryPoints } = usePlayerVictoryPoints(playerColor);
    const currentStateValidator = useCurrentStateValidator();
    const gameValidator = useGameValidator();
    const isCurrentTurnPlayer = currentStateValidator.currentTurnPlayers.includes(playerColor) && (currentStateValidator.currentTurnPlayers.length === 1 || playerColor === myPlayerColor);
    const isTakingOutOfTurnAction = isPlayerTakingAction && !isCurrentTurnPlayer;
    const isMechanicTurnColonistRushEnabled = useGameStore((state)=>{
        var _state_gameState_experimentalMechanicState;
        return ((_state_gameState_experimentalMechanicState = state.gameState.experimentalMechanicState) === null || _state_gameState_experimentalMechanicState === void 0 ? void 0 : _state_gameState_experimentalMechanicState.mechanicTurnColonistRushState) !== undefined;
    });
    const headerProps = useMemo(()=>{
        var _userState_profilePictureUrl;
        return {
            avatar: isActiveHumanSession == true ? (_userState_profilePictureUrl = userState.profilePictureUrl) !== null && _userState_profilePictureUrl !== void 0 ? _userState_profilePictureUrl : getLobbyImagePath(userState.databaseIcon) : getLobbyImagePath(LobbyImage.Bot),
            username: userState.username,
            pointsPublic: publicVictoryPoints,
            pointsPrivate: privateVictoryPoints - publicVictoryPoints
        };
    }, [
        userState,
        publicVictoryPoints,
        privateVictoryPoints,
        isActiveHumanSession
    ]);
    const playerStatus = usePlayerStatus(playerColor);
    const timeLeftInSeconds = useGameStore((state)=>state.timeLeftInSeconds);
    const isPaused = useGameStore((state)=>state.gamePause.isPaused);
    const liveTimeLeftInSeconds = useGameTimer({
        timeLeftInSeconds: timeLeftInSeconds,
        isPaused: isPaused
    });
    const turnState = useGameStore((state)=>state.gameState.currentState.turnState);
    const actionState = useGameStore((state)=>state.gameState.currentState.actionState);
    const playerActionState = currentStateValidator.getPlayerActionState(playerColor);
    const playersTakingAction = gameValidator.getPlayersTakingAction();
    const isWaitingForOtherPlayers = actionState !== ActionState.None && !playersTakingAction.includes(playerColor);
    const isMyPlayerTakingAction = playersTakingAction.includes(myPlayerColor);
    const footerValueProps = useMemo(()=>({
            text: playerStatus,
            timeLeftInSeconds: liveTimeLeftInSeconds,
            isLowTimeEnabled: !isReplay && !isMechanicTurnColonistRushEnabled && (isMyPlayer ? !isWaitingForOtherPlayers : isCurrentTurnPlayer && isMyPlayerTakingAction),
            hideTimer: isReplay || modeSetting === ModeSettingEnum.BeginnerModeClassic4P,
            hideText: isMechanicTurnColonistRushEnabled && playerActionState === ActionState.None || isWaitingForOtherPlayers
        }), [
        playerStatus,
        liveTimeLeftInSeconds,
        isMyPlayer,
        isMechanicTurnColonistRushEnabled,
        modeSetting,
        playerActionState,
        isWaitingForOtherPlayers,
        isCurrentTurnPlayer,
        isMyPlayerTakingAction,
        isReplay
    ]);
    const shouldRenderFooter = isCurrentTurnPlayer && turnState !== TurnState.SpecialBuildPhase;
    const footerProps = shouldRenderFooter ? footerValueProps : undefined;
    const isRequestingSpecialBuildPhase = useGameStore((state)=>{
        var _state_gameState_mechanicSpecialBuildPhaseState_playerStates_playerColor, _state_gameState_mechanicSpecialBuildPhaseState_playerStates, _state_gameState_mechanicSpecialBuildPhaseState;
        return ((_state_gameState_mechanicSpecialBuildPhaseState = state.gameState.mechanicSpecialBuildPhaseState) === null || _state_gameState_mechanicSpecialBuildPhaseState === void 0 ? void 0 : (_state_gameState_mechanicSpecialBuildPhaseState_playerStates = _state_gameState_mechanicSpecialBuildPhaseState.playerStates) === null || _state_gameState_mechanicSpecialBuildPhaseState_playerStates === void 0 ? void 0 : (_state_gameState_mechanicSpecialBuildPhaseState_playerStates_playerColor = _state_gameState_mechanicSpecialBuildPhaseState_playerStates[playerColor]) === null || _state_gameState_mechanicSpecialBuildPhaseState_playerStates_playerColor === void 0 ? void 0 : _state_gameState_mechanicSpecialBuildPhaseState_playerStates_playerColor.isRequesting) == true || false;
    });
    const hasMerchant = useGameStore((state)=>{
        var _state_gameState_mechanicMerchantState;
        return ((_state_gameState_mechanicMerchantState = state.gameState.mechanicMerchantState) === null || _state_gameState_mechanicMerchantState === void 0 ? void 0 : _state_gameState_mechanicMerchantState.owner) === playerColor;
    });
    const hasRobber = currentStateValidator.nextPlayerToMoveRobber === playerColor;
    const diceState = useGameStore((state)=>state.gameState.diceState);
    const eventDiceState = useGameStore((state)=>state.gameState.mechanicEventDiceState);
    const lastPlayerToRollDice = useGameStore((state)=>{
        var _state_gameState_mechanicSpecialBuildPhaseState;
        return (_state_gameState_mechanicSpecialBuildPhaseState = state.gameState.mechanicSpecialBuildPhaseState) === null || _state_gameState_mechanicSpecialBuildPhaseState === void 0 ? void 0 : _state_gameState_mechanicSpecialBuildPhaseState.lastPlayerToRollDice;
    });
    const isPlayerForDice = turnState === TurnState.SpecialBuildPhase ? lastPlayerToRollDice === playerColor : isCurrentTurnPlayer;
    const showDice = isPlayerForDice && !isMyPlayer && turnState !== TurnState.InitialPlacement;
    const dice = useMemo(()=>{
        if (!showDice) return undefined;
        const result = {
            dice1: diceState.dice1,
            dice2: diceState.dice2
        };
        if (eventDiceState !== undefined) {
            result.eventDice = eventDiceState.eventDice;
        }
        return result;
    }, [
        showDice,
        diceState.dice1,
        diceState.dice2,
        eventDiceState
    ]);
    const isInSpecialBuildPhase = isCurrentTurnPlayer && turnState === TurnState.SpecialBuildPhase;
    const extraFooterProps = useMemo(()=>{
        const props = {
            isSpecialBuildPhaseRequested: isRequestingSpecialBuildPhase,
            isMerchant: hasMerchant,
            isRushNextRobber: hasRobber,
            isWaiting: isTakingOutOfTurnAction,
            playerColor: playerColor,
            playerUsername: userState.username
        };
        if (dice !== undefined) {
            props.dice = dice;
        }
        if (isTakingOutOfTurnAction || isInSpecialBuildPhase) {
            const outOfTurnActionText = CurrentStateMessageUtils.getPlayerInfoExtraFooterActionStateText({
                turnState: turnState,
                actionState: playerActionState
            });
            if (outOfTurnActionText !== undefined) props.outOfTurnActionText = outOfTurnActionText;
            if (turnState === TurnState.SpecialBuildPhase) props.timeLeftInSeconds = liveTimeLeftInSeconds;
        }
        return props;
    }, [
        isRequestingSpecialBuildPhase,
        hasMerchant,
        hasRobber,
        isTakingOutOfTurnAction,
        playerColor,
        dice,
        isInSpecialBuildPhase,
        playerActionState,
        liveTimeLeftInSeconds,
        turnState
    ]);
    const { getPlayerValidator } = usePlayerValidators();
    const playerValidator = getPlayerValidator(playerColor);
    const headerWrapper = useCallback((headerElement)=>/*#__PURE__*/ React.createElement(UIGamePlayerOptionsDropdown, {
            playerValidator: playerValidator
        }, headerElement), [
        playerValidator
    ]);
    const sectionContentHandProps = usePlayerInfoSectionContentHandProps(playerColor);
    const sectionContentUsedDevsProps = usePlayerInfoSectionContentUsedDevsProps(playerColor);
    const sectionContentCKProps = usePlayerInfoSectionContentCKProps(playerColor);
    const sectionContentPiecesProps = usePlayerInfoSectionContentPiecesProps(playerColor);
    return /*#__PURE__*/ React.createElement(UIGamePlayerInfo, {
        playerColor: playerColor,
        header: headerProps,
        headerWrapper: headerWrapper,
        footer: footerProps,
        extraFooter: extraFooterProps
    }, visibleSections.includes('hand') && /*#__PURE__*/ React.createElement(UIGamePlayerInfoSection, null, /*#__PURE__*/ React.createElement(UIGamePlayerInfoSectionContentHand, sectionContentHandProps)), visibleSections.includes('pieces') && /*#__PURE__*/ React.createElement(UIGamePlayerInfoSection, null, /*#__PURE__*/ React.createElement(UIGamePlayerInfoSectionContentPieces, sectionContentPiecesProps)), visibleSections.includes('usedDevs') && sectionContentUsedDevsProps && /*#__PURE__*/ React.createElement(UIGamePlayerInfoSection, null, /*#__PURE__*/ React.createElement(UIGamePlayerInfoSectionContentUsedDevs, sectionContentUsedDevsProps)), visibleSections.includes('ck') && sectionContentCKProps && /*#__PURE__*/ React.createElement(UIGamePlayerInfoSection, null, /*#__PURE__*/ React.createElement(UIGamePlayerInfoSectionContentCK, sectionContentCKProps)));
};
